import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import mgmService from '../../../services/mgmService';

const COMPANIES = [
  {
    title: 'Сільпо',
    logo: getImageFromS3('/images/mgm/silpo.png'),
    glass: getImageFromS3('/images/mgm/silpo-glass.png')
  },
  {
    title: 'Eva',
    logo: getImageFromS3('/images/mgm/eva.png'),
    glass: getImageFromS3('/images/mgm/eva-glass.png')
  },
  {
    title: 'Megogo',
    logo: getImageFromS3('/images/mgm/megogo.png'),
    glass: getImageFromS3('/images/mgm/megogo-glass.png')
  },
  {
    title: 'Епіцентр',
    logo: getImageFromS3('/images/mgm/epicentr.png'),
    glass: getImageFromS3('/images/mgm/epicentr-glass.png')
  },
  {
    title: 'Ашан',
    logo: getImageFromS3('/images/mgm/auchan.png'),
    glass: getImageFromS3('/images/mgm/auchan-glass.png')
  },
  {
    title: 'Watsons',
    logo: getImageFromS3('/images/mgm/watsons.png'),
    glass: getImageFromS3('/images/mgm/watsons-glass.png')
  },
  {
    title: 'Фора',
    logo: getImageFromS3('/images/mgm/fora.png'),
    glass: getImageFromS3('/images/mgm/fora-glass.png')
  },
  {
    title: 'Метро',
    logo: getImageFromS3('/images/mgm/metro.png'),
    glass: getImageFromS3('/images/mgm/metro-glass.png')
  },
  {
    title: 'Розетка',
    logo: getImageFromS3('/images/mgm/rozetka.png'),
    glass: getImageFromS3('/images/mgm/rozetka-glass.png')
  },
  {
    title: 'Novus',
    logo: getImageFromS3('/images/mgm/novus.png'),
    glass: getImageFromS3('/images/mgm/rozetka-glass.png')
  }
];

const FILTERS = ['Всі', '50', '100', '200', '500', '1000'];

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getMonthlyRemaining = (balanceData) => {
  if (!balanceData) return null;

  const remaining = toNumber(
    balanceData.monthlyRemaining ?? balanceData.monthly_remaining
  );
  if (remaining !== null) return remaining;

  const limit = toNumber(balanceData.monthlyLimit ?? balanceData.monthly_limit);
  const spent = toNumber(balanceData.monthlySpent ?? balanceData.monthly_spent);
  if (limit !== null && spent !== null) return limit - spent;

  return null;
};

const PointChangeComponent = ({
  initialTab,
  setScreenNumber,
  onQuestionClick,
  onCertificateClick,
  onRewardClick
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'offers');
  const [activeFilter, setActiveFilter] = useState('Всі');
  const [activePartner, setActivePartner] = useState(null);
  const [offers, setOffers] = useState([]);
  const [myCertificates, setMyCertificates] = useState([]);
  const [balance, setBalance] = useState(0);
  const [monthlyRemaining, setMonthlyRemaining] = useState(null);

  useEffect(() => {
    (async () => {
      const balanceData = await mgmService.getBalance();
      setBalance(balanceData?.balance || 0);
      setMonthlyRemaining(getMonthlyRemaining(balanceData));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const nominal =
        activeFilter === 'Всі' ? undefined : parseInt(activeFilter, 10);
      const certificates = await mgmService.getCertificates(nominal);

      if (Array.isArray(certificates)) {
        // Show only partners flagged active & listable on the mgm certificate.
        // Guard: if the response doesn't carry these flags yet, don't blank the
        // list — fall back to showing everything.
        const hasVisibilityFlags = certificates.some(
          (cert) =>
            cert.is_active !== undefined && cert.is_discrete !== undefined
        );
        const visibleCertificates = hasVisibilityFlags
          ? certificates.filter(
              (cert) => cert.is_active === true && cert.is_discrete === true
            )
          : certificates;
        const mappedOffers = visibleCertificates.map((cert) => {
          const company = COMPANIES.find(
            (c) =>
              c.title.toLowerCase() === cert.partner_name?.toLowerCase() ||
              (c.title === 'Сільпо' &&
                cert.partner_name?.toLowerCase() === 'silpo') ||
              (c.title === 'Епіцентр' &&
                cert.partner_name?.toLowerCase() === 'epicentr') ||
              (c.title === 'Ашан' &&
                cert.partner_name?.toLowerCase() === 'auchan') ||
              (c.title === 'Фора' &&
                cert.partner_name?.toLowerCase() === 'fora') ||
              (c.title === 'Метро' &&
                cert.partner_name?.toLowerCase() === 'metro') ||
              (c.title === 'Розетка' &&
                cert.partner_name?.toLowerCase() === 'rozetka')
          );
          return {
            ...cert,
            id: cert.id,
            company: cert.partner_name,
            partnerTitle: company?.title,
            title: `Сертифікат на ${cert.nominal} грн у ${cert.partner_name}.`,
            description: cert.expiration_date
              ? `Дійсний до ${cert.expiration_date}.`
              : '',
            value: cert.nominal,
            currency: 'грн',
            points: cert.price_in_points,
            logo: company?.logo,
            glass: company?.glass
          };
        });
        setOffers(mappedOffers);
      }
    })();
  }, [activeFilter]);

  useEffect(() => {
    (async () => {
      const rewards = await mgmService.getRewards();

      if (Array.isArray(rewards)) {
        const mappedRewards = rewards.map((reward) => {
          const cert = reward.certificate || {};
          const company = COMPANIES.find(
            (c) =>
              c.title.toLowerCase() === cert.partner_name?.toLowerCase() ||
              (c.title === 'Сільпо' &&
                cert.partner_name?.toLowerCase() === 'silpo') ||
              (c.title === 'Епіцентр' &&
                cert.partner_name?.toLowerCase() === 'epicentr') ||
              (c.title === 'Ашан' &&
                cert.partner_name?.toLowerCase() === 'auchan') ||
              (c.title === 'Фора' &&
                cert.partner_name?.toLowerCase() === 'fora') ||
              (c.title === 'Метро' &&
                cert.partner_name?.toLowerCase() === 'metro') ||
              (c.title === 'Розетка' &&
                cert.partner_name?.toLowerCase() === 'rozetka')
          );
          return {
            ...reward,
            id: reward.id,
            company: cert.partner_name,
            title: `Сертифікат на ${cert.nominal} грн у ${cert.partner_name}.`,
            description: cert.expiration_date
              ? `Дійсний до ${cert.expiration_date}.`
              : '',
            value: cert.nominal,
            currency: 'грн',
            logo: company?.logo,
            glass: company?.glass
          };
        });
        setMyCertificates(mappedRewards);
      }
    })();
  }, []);

  const filteredOffers = activePartner
    ? offers.filter((offer) => offer.partnerTitle === activePartner)
    : offers;

  // Partner logos are derived from the visible certificates (not a hard-coded
  // list), so only partners with active & listable offers appear as filters.
  const partnerFilters = offers.reduce((acc, offer) => {
    const title = offer.partnerTitle;
    if (title && offer.logo && !acc.some((p) => p.title === title)) {
      acc.push({ title, logo: offer.logo });
    }
    return acc;
  }, []);

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <img
        className={styles.glassHeadBg}
        src={getImageFromS3('/images/mgm/glass-head-bg.png')}
        alt="glass-head"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setScreenNumber(1)}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div onClick={onQuestionClick}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className={styles.pointBalanceContainer}>
          <p>
            Баланс балів <b>{balance}</b>
          </p>
        </div>
        <h1>Обміняти бали</h1>
        <div className={styles.switcherContainer}>
          <div className={styles.switcher}>
            <div
              className={`${styles.switcherItem} ${
                activeTab === 'offers' ? styles.active : ''
              }`}
              onClick={() => setActiveTab('offers')}
            >
              Пропозиції
            </div>
            <div
              className={`${styles.switcherItem} ${
                activeTab === 'certificates' ? styles.active : ''
              }`}
              onClick={() => setActiveTab('certificates')}
            >
              Мої сертифікати
            </div>
          </div>
        </div>
        {activeTab === 'offers' && (
          <div className={styles.offersContent}>
            <div className={styles.filters}>
              {FILTERS.map((filter) => (
                <div
                  key={filter}
                  className={`${styles.filterItem} ${
                    activeFilter === filter ? styles.active : ''
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </div>
              ))}
            </div>
            <div className={styles.logos}>
              {partnerFilters.map((partner) => (
                <div
                  key={partner.title}
                  className={`${styles.logoItem} ${
                    activePartner === partner.title ? styles.active : ''
                  }`}
                  onClick={() =>
                    setActivePartner((prev) =>
                      prev === partner.title ? null : partner.title
                    )
                  }
                >
                  <img src={partner.logo} alt="logo" />
                </div>
              ))}
            </div>
            <div className={styles.offersList}>
              {filteredOffers.map((offer) => {
                const cost = Number(offer.points ?? offer.point_nominal ?? 0);
                const isWithinMonthlyLimit =
                  monthlyRemaining === null || cost <= monthlyRemaining;
                const isAffordable =
                  !(cost > 0) || (balance >= cost && isWithinMonthlyLimit);
                return (
                  <div
                    key={offer.id}
                    className={`${styles.card} ${
                      isAffordable ? '' : styles.disabled
                    }`}
                    onClick={() => isAffordable && onCertificateClick(offer)}
                  >
                    <div className={styles.cardHeader}>
                      <img
                        className={styles.glass}
                        src={offer?.glass}
                        alt="glass"
                      />
                      <img
                        className={styles.logo}
                        src={offer?.logo}
                        alt="logo"
                      />
                      <div className={styles.amount}>
                        {offer.value} <span>{offer.currency}</span>
                      </div>
                    </div>
                    <div className={styles.cardFooter}>
                      <p className={styles.cardTitle}>{offer.company}</p>
                      <p className={styles.cardDescription}>{offer.title}</p>
                      <p className={styles.cardPoints}>
                        {offer.point_nominal} балів
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {activeTab === 'certificates' && (
          <div className={styles.certificatesContent}>
            <div className={styles.certificatesList}>
              {myCertificates.map((cert) => (
                <div key={cert.id} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <img
                      className={styles.glass}
                      src={cert?.glass}
                      alt="glass"
                    />
                    <img className={styles.logo} src={cert?.logo} alt="logo" />
                    <div className={styles.amount}>
                      {cert.value} <span>{cert.currency}</span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <p className={styles.cardTitle}>{cert.company}</p>
                    <p className={styles.cardDescription}>
                      {cert.title}
                      <br />
                      {cert.description}
                    </p>
                    <button
                      className={styles.openButton}
                      onClick={() => onRewardClick(cert)}
                    >
                      Відкрити
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={styles.footer}>
          <button onClick={() => setScreenNumber(2)}>Правила програм</button>
          <button
            onClick={() =>
              window.open(
                'https://www.pmiprivacy.com/ukraine/uk/consumer/',
                '_blank'
              )
            }
          >
            Політика конфіденційності
          </button>
          <button onClick={() => setScreenNumber(10)}>FAQ</button>
          <button onClick={() => setScreenNumber(11)}>
            Параметри застосування файлів cookie
          </button>
        </div>
      </div>
    </div>
  );
};

export default PointChangeComponent;
